/**
 * @license
 * Copyright (c) 2011 %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

//  MIT License

//  Copyright (c) 2018 Andrew Branch

//  Permission is hereby granted, free of charge, to any person obtaining a copy
//  of this software and associated documentation files (the "Software"), to deal
//  in the Software without restriction, including without limitation the rights
//  to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
//  copies of the Software, and to permit persons to whom the Software is
//  furnished to do so, subject to the following conditions:

//  The above copyright notice and this permission notice shall be included in all
//  copies or substantial portions of the Software.

//  THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
//  IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
//  FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
//  AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
//  LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
//  OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
//  SOFTWARE.

type UnionToIntersection<U> = (U extends any ? (k: U) => void : never) extends (k: infer I) => void
  ? I
  : never;

/**
 * pushProps
 * @param target
 * @param key
 * @param value
 * @returns
 */
function pushProp(target: Record<string, any>, key: string, value: any): void {
  if (key === 'class') {
    const oldClass = target['class'];
    target['class'] = oldClass ? [oldClass, value].join(' ').trim() : value;
  } else if (key === 'style') {
    if (typeof value == 'object') {
      target['style'] = { ...target['style'], ...value };
    } else {
      throw new Error(
        `Unable to merge prop '${key}'. ` + `Only support 'style' objects not 'style' strings`
      );
    }
  } else if (typeof value === 'function') {
    type AnyFunc = (...args: any) => void;
    const oldFn = target[key] as AnyFunc | undefined;
    target[key] = oldFn
      ? (...args: any[]) => {
          oldFn(...args);
          (value as AnyFunc)(...args);
        }
      : value;
  } else if (
    // skip undefined values
    value === undefined ||
    // skip if same
    (typeof value !== 'object' && value === target[key])
  ) {
    return;
  } else if (!(key in target)) {
    target[key] = value;
  } else if (
    key === 'aria-describedby' ||
    key === 'aria-labelledby' ||
    key === 'aria-owns' ||
    key === 'aria-controls' ||
    key === 'aria-details'
  ) {
    // concatenate values together using a space delimiter as per
    // https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Attributes
    const oldAria = target[key];
    target[key] = oldAria ? [oldAria, value].join(' ').trim() : value;
  } else {
    // Currently the primary use of this utility is to merge together the spread properties returned by multiple hooks.
    // Given that we don't want to overwrite a property that was returned from one hook with the value from another hook.
    // Potentially it will break functionality of the hook whose value was overwritten. That is why we prefer to throw an error
    // instead of merging the given properties. We can revisit this if we ever run into a case where our hooks
    // produce conflicting properties.
    throw new Error(
      `Unable to merge prop '${key}'. ` +
        `Only support 'className', 'style', some aria properties, and event handlers`
    );
  }
}

/**
 * Merges allProps together:
 *  - duplicate className and class allProps concatenated
 *  - duplicate style allProps merged - note that only style objects supported at this point
 *  - duplicate functions chained
 * @param allProps Props to merge together.
 */
export function mergeProps<T extends { [key: string]: any }[]>(
  ...allProps: T
): {
  [K in keyof UnionToIntersection<T[number]>]: K extends 'class'
    ? string
    : K extends 'style'
    ? UnionToIntersection<T[number]>[K]
    : Exclude<Extract<T[number], { [Q in K]: unknown }>[K], undefined>;
} {
  if (allProps.length === 1) {
    return allProps[0] as any;
  }

  return allProps.reduce((merged, props: any) => {
    for (const key in props) {
      pushProp(merged, key, props[key]);
    }
    return merged;
  }, {}) as any;
}
